// client/src/pages/pmPortal/TenantNoticesPage.js
// A notice is shown with its authority and its day count beside it, because a deadline
// nobody can check is how a wrongful eviction starts. A resident who can read the rule
// the notice was served under can tell whether the date on it is right.
import React, { useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Table, Loading, Notice, money, fmtDate, titleCase }
    from '../../components/secure/pm/PMKit';

const TenantNoticesPage = () => {
    const api = usePmPortalApiClient();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get('/api/pm-portal/tenant/notices').then(setRows).catch(e => setError(e.message));
    }, [api]);

    if (error) return <Notice tone="bad" title="Could not load your notices" message={error} />;
    if (!rows) return <Loading />;

    const today = new Date().toISOString().slice(0, 10);

    return (
        <>
            <PageHeader
                title="Notices"
                subtitle="Formal notices served on your tenancy, with the rule each one was served under."
            />

            {rows.length === 0 && (
                <Notice tone="good" title="Nothing served" message="No formal notice has been served on your tenancy." />
            )}

            {rows.length > 0 && (
                <Panel title="Served notices">
                    <Table
                        columns={[
                            { key: 'served_on', label: 'Served', nowrap: true, render: r => fmtDate(r.served_on) },
                            { key: 'notice_type', label: 'Type', render: r => titleCase(r.notice_type) },
                            { key: 'required_days', label: 'Days given', align: 'right',
                              render: r => (r.required_days ? `${r.required_days}` : '—') },
                            { key: 'effective_on', label: 'Effective', nowrap: true,
                              render: r => <span className={r.effective_on && r.effective_on >= today ? 'font-semibold' : ''}>{fmtDate(r.effective_on)}</span> },
                            { key: 'amount', label: 'Amount', align: 'right', render: r => money(r.amount) },
                            { key: 'method', label: 'Served by', render: r => (r.method ? titleCase(r.method) : '—') },
                            { key: 'authority', label: 'Under',
                              render: r => <span className="text-slate-600 text-xs">{r.authority || '—'}</span> },
                        ]}
                        rows={rows}
                        rowTone={r => (r.effective_on && r.effective_on >= today ? 'warn' : null)}
                    />
                </Panel>
            )}

            {rows.length > 0 && (
                <p className="text-xs text-slate-500 mt-2">
                    If you believe a notice or a date on it is wrong, raise it with the managing broker
                    in writing. Free legal advice may be available from a tenants&rsquo; rights organisation
                    in your municipality.
                </p>
            )}
        </>
    );
};

export default TenantNoticesPage;
